import React, { useState } from "react";

// =============================================================================
// CopyConversationButton
// =============================================================================
// PURPOSE: Clipboard copy button for DM conversations.
// Pattern matches OmniaTaskModal (text swap + timeout, no toast).
// Variant 'header' = larger, used in modal headers.
// Variant 'card' = compact, used in conversation card rows.
//
// Styling: light-mode-only glass system. Uses bg-black/[0.05] tints (not
// bg-white/N — white-on-white is invisible on bg-offwhite). text-glass-secondary
// for default state, text-glass on hover.
// =============================================================================

interface CopyConversationButtonProps {
  getText: () => string;
  variant?: "header" | "card";
  disabled?: boolean;
}

export const CopyConversationButton: React.FC<CopyConversationButtonProps> = ({
  getText,
  variant = "header",
  disabled,
}) => {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const text = getText();
      if (!text) return;
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setError(false);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Fallback: execCommand for environments without clipboard API (e.g. HTTP)
      try {
        const textarea = document.createElement("textarea");
        textarea.value = getText();
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {
        setError(true);
        setTimeout(() => setError(false), 2000);
      }
    }
  };

  // Header: slightly larger pill with border — matches OmniaTaskModal copy button feel
  // Card: compact, no border — fits inline in tight row layouts
  const baseClasses =
    variant === "header"
      ? "anim-tap token-11 font-medium px-3 py-1.5 rounded-lg bg-black/[0.05] hover:bg-black/[0.08] text-glass-secondary hover:text-glass transition-all border border-black/[0.05]"
      : "anim-tap token-10 px-2 py-1 rounded-md bg-black/[0.05] hover:bg-black/[0.08] text-glass-secondary hover:text-glass transition-all";

  const label = error ? "✗ Failed" : copied ? "✓ Copied!" : "Copy";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={`${baseClasses} ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
      title={disabled ? "No messages to copy" : "Copy conversation to clipboard"}
    >
      {label}
    </button>
  );
};
